import React, { useMemo, useRef, useState } from 'react';
import { startCase } from 'lodash';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import type { StylesConfig } from 'react-select';
import { Link, useRouteMatch } from 'react-router-dom';

import { Button } from 'components/ui/button';
import { Avatar } from '@buildoutinc/blueprint-react/ui/Avatar';
import { Badge } from '@buildoutinc/blueprint-react/ui/Badge';
import { Dialog } from '@buildoutinc/blueprint-react/ui/Dialog';
import { Offcanvas } from '@buildoutinc/blueprint-react/ui/Offcanvas';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faCircleXmark, faTrash, faUser } from '@fortawesome/pro-regular-svg-icons';

import ErrorBoundary from 'components/error_boundary';
import DateWithHover from 'components/timestamps/date_with_hover';
import { Select } from 'components/ui/select';
import { showSnackbar } from 'components/ui/snackbar/helper';
import DocAccessLevelSelect from 'bundles/leads/index/table/cells/doc_access_level';
import DocumentProgress from 'bundles/leads/index/table/cells/document_progress';
import LeadClientReportNotes from './show/client_report_notes';
import EditContactLink from './show/edit_contact_link';
import { databaseLinks, marketingPreferencesLabel } from './show/info';
import InviteToViewMenu from './show/invite_to_view_menu';
import LeadReferralSourceSelect from './show/lead_referral_source_select';
import LeadStatusSelect from './show/lead_status_select';
import LeadVaultProgress from './show/lead_vault_progress';
import PVPActivity from './show/pvp_activity';
import RequestMoreInformationNotes from './show/request_more_information_notes';

export interface InquiryPanelLead {
  id: number;
  accountStatus?: string | null;
  contactCompany?: string | null;
  contactPath?: string | null;
  contactPhoneNumber?: string | null;
  createdAt?: string | null;
  displayName: string;
  docAccessLevelForLease?: string | null;
  docAccessLevelForSale?: string | null;
  emailAddress?: string | null;
  expirationDate1031?: string | null;
  is1031Profile?: boolean | null;
  leadData: {
    email?: string | null;
    firstName?: string | null;
    initials?: string | null;
    jobTitle?: string | null;
    lastName?: string | null;
    name?: string | null;
  };
  leaseVaultGatesAccessLevelWithoutOverride?: string | null;
  spaceLabel?: string | null;
  marketingPreferences?: string[];
  oneTimeLinkSentAt?: string | null;
  profileUserType?: string | null;
  projectId: number;
  propertyVaultProfiles: {
    activityPath?: string | null;
    caSigned?: boolean | null;
    id?: number;
    saleOrLease: string | null;
  }[];
  referralSourceFixed?: boolean | null;
  referralSourceIcon?: string | null;
  referralSourceLabel?: string | null;
  requestMoreInformationNotes: { createdAt: string; message: string }[];
  saleVaultGatesAccessLevelWithoutOverride?: string | null;
  shareLinks?: { label: string; url: string }[] | null;
  source?: string | null;
  statusLabel?: string | null;
  updatedAt?: string | null;
  useVaultGates?: boolean;
  user?: { name?: string | null } | null;
}

interface InquiryPanelProject {
  brokersForShareDocs?: unknown[];
  id: number;
  useVaultGates?: boolean;
}

interface Pager {
  count: number;
  position: number;
  onNext: () => void;
  onPrevious: () => void;
}

interface Props {
  canDisplayEmailLink: boolean;
  contactPath?: string;
  deleting: boolean;
  features: { clientReport?: boolean; crm?: boolean; leadInviteActivity?: boolean };
  lead: InquiryPanelLead | null;
  leaseChecked: boolean;
  leaseOnly: boolean;
  pager?: Pager;
  project: InquiryPanelProject;
  refreshLead: (lead: InquiryPanelLead) => void;
  saleChecked: boolean;
  show1031: boolean;
  showSpace: boolean;
  showUpdateLeadModal: (lead: InquiryPanelLead) => void;
  onClose: () => void;
  onDelete: (lead: InquiryPanelLead) => void;
}

type Side = 'Lease' | 'Sale';
type OpenChangeDetails = Parameters<NonNullable<React.ComponentProps<typeof Offcanvas>['onOpenChange']>>[1];

interface SaveMessage {
  text: string;
  type: 'error' | 'success';
}

const blank = <span className="text-muted">—</span>;

const panelError = (
  <p className="text-muted mb-0">These inquiry details couldn&apos;t be shown. Refresh the page to try again.</p>
);

function showSaveMessage({ text, type }: SaveMessage) {
  showSnackbar({ text, variant: type === 'error' ? 'danger' : 'success' });
}

function PanelSelect({ styles, ...props }: React.ComponentProps<typeof Select>) {
  const panelStyles: StylesConfig = {
    ...styles,
    menu: (base, state) => ({
      ...(styles?.menu ? styles.menu(base, state) : base),
      minWidth: '100%',
      width: 'max-content'
    })
  };

  return <Select {...props} alignRight styles={panelStyles} />;
}

interface RowProps {
  control?: React.ReactNode;
  label: string;
  value?: React.ReactNode;
}

function Row({ control, label, value }: RowProps) {
  return (
    <div className="d-flex align-items-center justify-content-between gap-3 border-bottom py-2">
      <span className="text-muted text-nowrap">{label}</span>
      {control
        ? <div style={{ width: '10rem' }}>{control}</div>
        : <span className="fw-semibold text-end" style={{ minWidth: 0 }}>{value || blank}</span>}
    </div>
  );
}

function Section({ children, title }: { children: React.ReactNode; title: string }) {
  return (
    <div>
      <div className="fw-semibold fs-large mb-1">{title}</div>
      {children}
    </div>
  );
}

export function hides1031(project: { saleListingUrl?: string | null }) {
  return !project.saleListingUrl;
}

function date(value?: string | null) {
  return value ? <DateWithHover timestamp={value} /> : null;
}

function initials(lead: InquiryPanelLead) {
  if (lead.leadData.initials) return lead.leadData.initials;

  const words = (lead.leadData.name || '').split(/\s+/).filter(Boolean);

  return words.slice(0, 2).map(word => word[0].toUpperCase()).join('');
}

function roleJobTitle(lead: InquiryPanelLead) {
  return [startCase(lead.profileUserType || ''), startCase(lead.leadData.jobTitle || '')]
    .filter(part => part.trim().length > 0)
    .join(' / ');
}

function addedBy(lead: InquiryPanelLead) {
  if (lead.referralSourceIcon) return lead.referralSourceLabel;
  if (lead.source === 'connect') return 'Connect';
  if (lead.source === 'web_activity') return 'Web Activity';

  return lead.user?.name;
}

interface DocAccessLevelRowProps {
  lead: InquiryPanelLead;
  side: Side;
  onPinMenuToggle: (open: boolean) => void;
}

function DocAccessLevelRow({ lead, side, onPinMenuToggle }: DocAccessLevelRowProps) {
  const columnName = `docAccessLevelFor${side}`;
  const label = `${side} Doc Access Level`;

  if (lead.useVaultGates) {
    const progress = (
      <DocumentProgress
        columnName={columnName}
        idPrefix="inquiry-panel-"
        inlineMenu
        lead={lead}
        onMenuToggle={onPinMenuToggle}
        onSaveMessage={showSaveMessage}
      />
    );

    return <Row label={label} value={progress} />;
  }

  const select = (
    <DocAccessLevelSelect
      ariaLabel={label}
      columnName={columnName}
      lead={lead}
      selectComponent={PanelSelect}
      onSaveMessage={showSaveMessage}
    />
  );

  return <Row control={select} label={label} />;
}

export default function InquiryPanel({
  canDisplayEmailLink, contactPath, deleting, features, lead, leaseChecked, leaseOnly, pager, project, refreshLead,
  saleChecked, show1031, showSpace, showUpdateLeadModal, onClose, onDelete
}: Props) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [menuContainer, setMenuContainer] = useState<HTMLDivElement | null>(null);
  const [openPinMenus, setOpenPinMenus] = useState<Partial<Record<Side, boolean>>>({});
  const cancelledEscape = useRef<Event | null>(null);
  const { url } = useRouteMatch();
  const propertyVaultProfiles = lead?.propertyVaultProfiles;
  const vaultProfiles = useMemo(
    () => propertyVaultProfiles?.filter(pvp => (leaseOnly ? pvp.saleOrLease === 'Lease' : pvp.saleOrLease)) || [],
    [propertyVaultProfiles, leaseOnly]
  );

  if (!lead) return null;

  const vaultLead = { ...lead, propertyVaultProfiles: vaultProfiles };

  const name = lead.leadData.name;
  const role = roleJobTitle(lead);
  const verified = lead.accountStatus === 'Verified';
  const phone = lead.contactPhoneNumber
    ? (formatPhoneNumberIntl(lead.contactPhoneNumber) || lead.contactPhoneNumber)
    : null;
  const exchange1031 = typeof lead.is1031Profile === 'boolean' ? (lead.is1031Profile ? 'Yes' : 'No') : null;
  const database = databaseLinks(features.crm, lead);

  const togglePinMenu = (side: Side) => (open: boolean) => setOpenPinMenus(menus => ({ ...menus, [side]: open }));

  const handleOpenChange = (open: boolean, details: OpenChangeDetails) => {
    const pinMenuOpen = Object.values(openPinMenus).some(Boolean);
    const modalOpen = !!document.querySelector('.modal.show');
    const escapeForChild = details.event === cancelledEscape.current || pinMenuOpen || modalOpen;
    if (details.reason === 'escape-key' && escapeForChild) {
      cancelledEscape.current = details.event;
      details.cancel();
      details.allowPropagation();
      return;
    }
    if (!open) onClose();
  };

  const handleDelete = () => {
    setConfirmingDelete(false);
    onDelete(lead);
  };

  return (
    <>
      <Offcanvas open onOpenChange={handleOpenChange}>
        <Offcanvas.Content
          aria-label={`${name} inquiry`}
          initialFocus={() => menuContainer?.querySelector('button') ?? true}
          side="right"
          style={{ width: 'min(52rem, 100vw)' }}
        >
          <Offcanvas.Header className="d-flex flex-column align-items-stretch gap-3">
            <div className="d-flex align-items-center gap-3">
              <Avatar size="lg">
                <Avatar.Fallback className="fw-semibold">{initials(lead)}</Avatar.Fallback>
              </Avatar>
              <div style={{ minWidth: 0 }}>
                <Offcanvas.Title className="fs-4 fw-semibold mb-0 text-truncate">{name}</Offcanvas.Title>
                <div className="text-muted text-truncate">
                  {[role, lead.contactCompany].filter(Boolean).join(' · ') || '—'}
                </div>
              </div>
              {pager && pager.count > 1 && (
                <div className="d-inline-flex align-items-center gap-1 border rounded ms-auto align-self-start">
                  <Button
                    aria-label="Previous inquiry"
                    disabled={pager.position === 1}
                    icon={faChevronLeft}
                    size="sm"
                    tooltip="Previous inquiry"
                    variant="ghost"
                    onClick={pager.onPrevious}
                  />
                  <span className="fs-small text-muted text-nowrap">{pager.position} of {pager.count}</span>
                  <Button
                    aria-label="Next inquiry"
                    disabled={pager.position === pager.count}
                    icon={faChevronRight}
                    size="sm"
                    tooltip="Next inquiry"
                    variant="ghost"
                    onClick={pager.onNext}
                  />
                </div>
              )}
            </div>
            <div className="d-flex flex-wrap align-items-center gap-2">
              <Badge
                appearance="muted"
                variant={!lead.statusLabel || lead.statusLabel === 'No Status' ? 'secondary' : 'primary'}
              >
                {lead.statusLabel || 'No Status'}
              </Badge>
              {lead.accountStatus && (
                <Badge appearance="muted" variant="secondary">
                  {verified ? 'Verified' : <><FontAwesomeIcon icon={faCircleXmark} />Not Verified</>}
                </Badge>
              )}
              {showSpace && lead.spaceLabel && (
                <Badge appearance="muted" variant="secondary">{lead.spaceLabel}</Badge>
              )}
              <div className="ms-auto" ref={setMenuContainer}>
                <InviteToViewMenu
                  compact
                  features={features}
                  lead={lead}
                  menuContainer={menuContainer}
                  project={project}
                  refreshLead={refreshLead}
                  shareLinkSide={leaseOnly ? 'Lease' : undefined}
                  showUpdateLeadModal={showUpdateLeadModal}
                  useVaultGates={!!project.useVaultGates}
                />
              </div>
            </div>
          </Offcanvas.Header>

          <Offcanvas.Body className="d-flex flex-column gap-4" key={lead.id}>
            <ErrorBoundary fallback={panelError}>
              {vaultLead.propertyVaultProfiles.length > 0 && (
                <div className="d-flex flex-column gap-3">
                  <LeadVaultProgress compact lead={vaultLead} />
                </div>
              )}

              <Section title="Contact">
                <Row label="First Name" value={lead.leadData.firstName} />
                <Row label="Last Name" value={lead.leadData.lastName} />
                <Row
                  label="Email"
                  value={lead.emailAddress && (canDisplayEmailLink
                    ? <a href={`mailto:${lead.emailAddress}`}>{lead.emailAddress}</a>
                    : lead.emailAddress)}
                />
                <Row label="Phone" value={phone && <a href={`tel:${lead.contactPhoneNumber}`}>{phone}</a>} />
                <Row label="Job Title" value={role} />
                <Row label="Company Name" value={lead.contactCompany} />
                <Row label="Marketing Preferences" value={marketingPreferencesLabel(lead.marketingPreferences)} />
                {show1031 && (
                  <>
                    <Row label="1031 Exchange" value={exchange1031} />
                    <Row label="1031 Expiration" value={date(lead.expirationDate1031)} />
                  </>
                )}
                {database && <Row label="Database" value={database} />}
              </Section>

              <Section title="Inquiry">
                <Row
                  control={
                    <LeadStatusSelect
                      ariaLabel="Inquiry Status"
                      lead={lead}
                      selectComponent={PanelSelect}
                      onSaveMessage={showSaveMessage}
                    />
                  }
                  label="Inquiry Status"
                />
                {showSpace && lead.spaceLabel && <Row label="Space" value={lead.spaceLabel} />}
                {lead.referralSourceFixed ? (
                  <Row label="Referral Source" value={lead.referralSourceLabel} />
                ) : (
                  <Row
                    control={
                      <LeadReferralSourceSelect
                        ariaLabel="Referral Source"
                        lead={lead}
                        selectComponent={PanelSelect}
                        onSaveMessage={showSaveMessage}
                      />
                    }
                    label="Referral Source"
                  />
                )}
                <Row label="Link Sent" value={date(lead.oneTimeLinkSentAt)} />
                <Row label="Added By" value={addedBy(lead)} />
                <Row label="Date Added" value={date(lead.createdAt)} />
                <Row label="Last Updated" value={date(lead.updatedAt)} />
              </Section>

              <Section title="Access">
                {saleChecked && !leaseOnly && (
                  <DocAccessLevelRow lead={lead} side="Sale" onPinMenuToggle={togglePinMenu('Sale')} />
                )}
                {leaseChecked && (
                  <DocAccessLevelRow lead={lead} side="Lease" onPinMenuToggle={togglePinMenu('Lease')} />
                )}
                <Row label="Account Status" value={lead.accountStatus} />
                <PVPActivity compact lead={vaultLead} />
              </Section>

              {features.clientReport && <LeadClientReportNotes lead={lead} />}
              <RequestMoreInformationNotes lead={lead} />
            </ErrorBoundary>
          </Offcanvas.Body>

          <Offcanvas.Footer className="d-flex flex-wrap justify-content-between gap-2">
            <Offcanvas.Close render={<Button variant="ghost">Close</Button>} />
            <div className="d-flex flex-wrap justify-content-end gap-2">
              <EditContactLink lead={lead} />
              <Button
                disabled={deleting}
                icon={faTrash}
                label="Delete Inquiry"
                variant="destructive"
                onClick={() => setConfirmingDelete(true)}
              />
              <Button
                icon={faUser}
                label="View Contact"
                render={<Link to={contactPath ?? `${url}/${lead.id}`} />}
                variant="primary"
              />
            </div>
          </Offcanvas.Footer>
        </Offcanvas.Content>
      </Offcanvas>

      <Dialog open={confirmingDelete} onOpenChange={setConfirmingDelete}>
        <Dialog.Content>
          <Dialog.Header>
            <Dialog.Title>Delete this inquiry?</Dialog.Title>
          </Dialog.Header>
          <Dialog.Body>
            <p className="mb-0">{name} will be removed from this deal&rsquo;s inquiries.</p>
            <p className="text-muted mb-0 mt-2">
              The contact record stays in your book of business — only the inquiry is deleted.
            </p>
          </Dialog.Body>
          <Dialog.Footer>
            <Dialog.Cancel>Cancel</Dialog.Cancel>
            <Button icon={faTrash} label="Delete Inquiry" variant="destructive" onClick={handleDelete} />
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    </>
  );
}
